import { useCallback, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { message } from 'antd';
import { GET_PORTAL_ORDER_EXPORT_DOWNLOAD_URL } from 'graphql/queries/orderExport';
import { PortalOrderExportDownload } from 'graphql/types';
import { useOrderExportError } from 'hooks/useOrderExportError';

/**
 * 取导出文件的下载链接并打开。
 *
 * <p>等待弹窗与导出记录页共用：两处的失败分支必须完全一致，否则同一个 TASK_NOT_READY
 * 在弹窗里提示「稍候」、在记录页里提示「文件已失效」，客户会以为是两个不同的问题。
 *
 * <p>链接每次按需签发、不缓存（fetchPolicy: no-cache）：URL 有 7 天有效期，缓存住的旧 URL
 * 过期后点了就是 403，而重新查一次本来就能拿到新的。
 */
export const useOrderExportDownload = () => {
    const translateError = useOrderExportError();
    const [downloadingTaskId, setDownloadingTaskId] = useState<string | null>(null);
    const [fetchDownloadUrl] = useLazyQuery<{ portalOrderExportDownloadUrl: PortalOrderExportDownload }>(
        GET_PORTAL_ORDER_EXPORT_DOWNLOAD_URL,
        { fetchPolicy: 'no-cache' }
    );

    const download = useCallback(
        async (taskId: string) => {
            setDownloadingTaskId(taskId);
            try {
                const { data, error } = await fetchDownloadUrl({ variables: { taskId } });
                /*
                 * 网络异常与 GraphQL 错误已由 errorLink 统一弹过提示，这里再弹一次
                 * 客户会连着看到两条内容不同的报错。业务失败（success=false）不走这条路径。
                 */
                if (error) return;

                const result = data?.portalOrderExportDownloadUrl;
                if (!result?.success || !result.url) {
                    message.error(translateError(result?.errorCode, result?.messageKey));
                    return;
                }
                window.open(result.url, '_blank');
            } finally {
                setDownloadingTaskId(null);
            }
        },
        [fetchDownloadUrl, translateError]
    );

    return { download, downloadingTaskId };
};
